import React, {useCallback, useEffect, useState} from "react";
import {Navigate} from "react-router-dom";
import {useSelector} from "react-redux";
import Pagination from "react-js-pagination";
import {Button, Modal} from "react-bootstrap";
import {toast, ToastContainer} from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import partsService from "../../services/parts.service";
import Toaster from "../../common/ToastMsg";

const PartsAiResult = () => {
    const {user: currentUser} = useSelector((state) => state.auth);
    const isAdmin = currentUser?.roles?.includes("ROLE_ADMIN");
    const [items, setItems] = useState([]);
    const [pageRequest, setPageRequest] = useState({page: 0, size: 50});
    const [totalItems, setTotalItems] = useState(0);
    const [loading, setLoading] = useState(false);
    const [confirmingId, setConfirmingId] = useState(null);
    const [selectedIds, setSelectedIds] = useState([]);
    const [confirmDialogItems, setConfirmDialogItems] = useState([]);
    const [bulkConfirming, setBulkConfirming] = useState(false);

    const loadResults = useCallback(() => {
        setLoading(true);
        partsService.getPendingAiResults(pageRequest.page, pageRequest.size).then(
            response => {
                setItems(response.data.content || []);
                setTotalItems(response.data.totalElements || 0);
                setSelectedIds([]);
                setLoading(false);
            },
            error => {
                setLoading(false);
                Toaster.error({
                    title: "Failed to load AI results!",
                    text: error.response?.data || error.message
                }, {
                    position: "top-center",
                    containerId: "parts-ai-result"
                });
            }
        );
    }, [pageRequest.page, pageRequest.size]);

    useEffect(() => {
        if (isAdmin) {
            loadResults();
        }
    }, [isAdmin, loadResults]);

    if (!currentUser) {
        return <Navigate to="/login" />;
    }
    if (!isAdmin) {
        return <Navigate to="/profile" />;
    }

    const handlePageChange = (pageNumber) => {
        setPageRequest(prev => ({...prev, page: pageNumber - 1}));
    };

    const handlePageSizeChange = (event) => {
        setPageRequest({page: 0, size: Number(event.target.value)});
    };

    const toggleSelect = (partsListId) => {
        setSelectedIds(prev => prev.includes(partsListId)
            ? prev.filter(id => id !== partsListId)
            : [...prev, partsListId]);
    };

    const visibleIds = items.map(item => item.partsListId);
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selectedIds.includes(id));

    const toggleSelectAllVisible = () => {
        if (allVisibleSelected) {
            setSelectedIds(prev => prev.filter(id => !visibleIds.includes(id)));
            return;
        }
        setSelectedIds(prev => Array.from(new Set([...prev, ...visibleIds])));
    };

    const openConfirmDialog = (itemsToConfirm) => {
        setConfirmDialogItems(itemsToConfirm);
    };

    const closeConfirmDialog = () => {
        if (!bulkConfirming && confirmingId === null) {
            setConfirmDialogItems([]);
        }
    };

    const confirmDialogResults = () => {
        const itemsToConfirm = confirmDialogItems;
        if (itemsToConfirm.length === 0) {
            return;
        }

        const isBulk = itemsToConfirm.length > 1;
        setBulkConfirming(isBulk);
        setConfirmingId(isBulk ? null : itemsToConfirm[0].partsListId);

        Promise.all(itemsToConfirm.map(item => partsService.confirmAiResult(item.partsListId))).then(
            () => {
                setBulkConfirming(false);
                setConfirmingId(null);
                setConfirmDialogItems([]);
                setSelectedIds([]);
                toast.info(`${itemsToConfirm.length} AI result${itemsToConfirm.length > 1 ? "s" : ""} confirmed.`, {
                    position: "top-center",
                    containerId: "parts-ai-result",
                    autoClose: 2000
                });
                loadResults();
            },
            error => {
                setBulkConfirming(false);
                setConfirmingId(null);
                Toaster.error({
                    title: "Failed to confirm AI result!",
                    text: error.response?.data || error.message
                }, {
                    position: "top-center",
                    containerId: "parts-ai-result"
                });
            }
        );
    };

    const renderSources = (sources) => {
        if (!sources || sources.length <= 0) {
            return <span className="text-muted">No sources</span>;
        }
        return (
            <ul className="mb-0 ps-3">
                {sources.map((source, index) => (
                    <li key={`${source.url}-${index}`}>
                        <a href={source.url} target="_blank" rel="noopener noreferrer">
                            {source.title || source.url}
                        </a>
                    </li>
                ))}
            </ul>
        );
    };

    return (
        <div className="container-fluid">
            <ToastContainer containerId="parts-ai-result" />
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h3>Parts Number AI Result</h3>
                <div className="d-flex align-items-center gap-2">
                    {loading && (
                        <span>
                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                            Loading...
                        </span>
                    )}
                    <button
                        type="button"
                        className="btn btn-success btn-sm"
                        disabled={selectedIds.length === 0 || bulkConfirming}
                        onClick={() => openConfirmDialog(items.filter(item => selectedIds.includes(item.partsListId)))}
                    >
                        {bulkConfirming && (
                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        )}
                        Confirm Selected ({selectedIds.length})
                    </button>
                </div>
            </div>
            <div className="table-responsive">
                <table className="table table-striped align-middle">
                    <thead>
                    <tr>
                        <th style={{width: "44px"}}>
                            <input
                                type="checkbox"
                                className="form-check-input"
                                checked={allVisibleSelected}
                                onChange={toggleSelectAllVisible}
                                disabled={items.length === 0 || loading}
                                aria-label="Select all visible AI results"
                            />
                        </th>
                        <th>PN</th>
                        <th>Description</th>
                        <th>Sources</th>
                        <th>Updated</th>
                    </tr>
                    </thead>
                    <tbody>
                    {!loading && items.length === 0 && (
                        <tr>
                            <td colSpan="5" className="text-center text-muted">No AI results need confirmation.</td>
                        </tr>
                    )}
                    {items.map(item => (
                        <tr key={item.partsListId}>
                            <td>
                                <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={selectedIds.includes(item.partsListId)}
                                    onChange={() => toggleSelect(item.partsListId)}
                                    disabled={confirmingId === item.partsListId || bulkConfirming}
                                    aria-label={`Select AI result ${item.pnClean} ${item.partsDesc}`}
                                />
                            </td>
                            <td>{item.pnClean}</td>
                            <td>{item.partsDesc}</td>
                            <td>{renderSources(item.sources)}</td>
                            <td>{item.updatedAt ? new Date(item.updatedAt).toLocaleString() : ""}</td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
            <div className="d-flex justify-content-center">
                <Pagination
                    activePage={pageRequest.page + 1}
                    itemsCountPerPage={pageRequest.size}
                    totalItemsCount={totalItems}
                    pageRangeDisplayed={10}
                    onChange={handlePageChange}
                    itemClass="page-item"
                    linkClass="page-link"
                />
                <div className="dropdown m-2 mt-0">
                    <select
                        value={pageRequest.size}
                        className="form-select"
                        onChange={handlePageSizeChange}
                    >
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                        <option value={200}>200</option>
                    </select>
                </div>
            </div>
            <Modal show={confirmDialogItems.length > 0} onHide={closeConfirmDialog} centered>
                <Modal.Header closeButton={!bulkConfirming && confirmingId === null}>
                    <Modal.Title>Confirm AI Result</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        Confirm {confirmDialogItems.length} AI result{confirmDialogItems.length > 1 ? "s" : ""}?
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeConfirmDialog} disabled={bulkConfirming || confirmingId !== null}>
                        Cancel
                    </Button>
                    <Button variant="success" onClick={confirmDialogResults} disabled={bulkConfirming || confirmingId !== null}>
                        {(bulkConfirming || confirmingId !== null) && (
                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        )}
                        Confirm
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default PartsAiResult;
